import {
    AfterViewInit,
    Component,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { GoApplicationPreparationBaseComponent } from '../shared/components/go-application-preparation-base/go-application-preparation-base.component';
import { GoApplicationPurposeCodeEnum } from '../shared/constants/go-application-purpose-code-enum';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { MatInputModule } from '@angular/material/input';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { MatIconModule } from '@angular/material/icon';
import {
    MatExpansionModule,
    MatExpansionPanel,
} from '@angular/material/expansion';
import {
    MatRadioButton,
    MatRadioChange,
    MatRadioModule,
} from '@angular/material/radio';
import { GoPurposeOfFilingFormModel } from './models/go-purpose-of-filing-form-model';
import { PurposeOfFilingErrorMessages } from '../../shared/constants/purpose-of-filing-validation-configurations';
import { HtmlInputConstants } from '../../../../core/constants/html-input-constants';
import { IMaskConstants } from '../../../../core/constants/imask';
import { IMaskDirective } from 'angular-imask';
import { Router, RouterModule } from '@angular/router';
import { GoApplicationRequestFormModel } from '../shared/models/go-application-request-form-model';
import { ULSService } from '../../../../core/services/uls/uls.service';
import { GoStorageService } from '../go-parameters/services/go-storage/go-storage.service';
import { GoApplicationPreparationService } from '../shared/services/go-application-preparation/go-application-preparation.service';
import { ApplicationPageFacadeService } from '../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { GoApplicationService } from '../shared/services/go-application/go-application.service';
import { firstValueFrom, of, switchMap, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'app-go-purpose-of-filing',
    imports: [
        MatRadioModule,
        MatExpansionModule,
        MatIconModule,
        FormsModule,
        RegexInputDirective,
        MatInputModule,
        ReactiveFormsModule,
        CommonModule,
        MatFormFieldModule,
        IMaskDirective,
        RouterModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './go-purpose-of-filing.component.html',
})
export class GoPurposeOfFilingComponent
    extends GoApplicationPreparationBaseComponent<GoPurposeOfFilingFormModel>
    implements AfterViewInit
{
    @ViewChild('newLicenseExpansionPanel')
    newLicenseExpansionPanel!: MatExpansionPanel;
    @ViewChild('newLocationExpansionPanel')
    newLocationExpansionPanel!: MatExpansionPanel;
    @ViewChild('newFrequenciesExpansionPanel')
    newFrequenciesExpansionPanel!: MatExpansionPanel;

    @ViewChild('newLicenseRadioButton')
    newLicenseRadioButton!: MatRadioButton;
    @ViewChild('newLocationRadioButton')
    newLocationRadioButton!: MatRadioButton;
    @ViewChild('newFrequenciesRadioButton')
    newFrequenciesRadioButton!: MatRadioButton;

    form: FormGroup<FormGroupType<GoPurposeOfFilingFormModel>> =
        this.getFormDefinition();

    constructor(
        protected override readonly goApplicationPreparationService: GoApplicationPreparationService,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        protected override readonly goApplicationService: GoApplicationService,
        protected override readonly router: Router,
        protected override readonly goStorageService: GoStorageService,
        private readonly ulsService: ULSService,
    ) {
        super(
            goApplicationPreparationService,
            applicationPageFacadeService,
            goApplicationService,
            router,
            goStorageService,
        );
    }

    getFormDefinition(): FormGroup<FormGroupType<GoPurposeOfFilingFormModel>> {
        const goApplicationRequestFormDefinition =
            this.goApplicationPreparationService.getFormDefinition();

        return new FormGroup<FormGroupType<GoPurposeOfFilingFormModel>>({
            applicationPurpose:
                goApplicationRequestFormDefinition.controls.applicationPurpose,
            callSign: goApplicationRequestFormDefinition.controls.callSign,
            fccRegistrationNumber:
                goApplicationRequestFormDefinition.controls
                    .fccRegistrationNumber,
        });
    }

    hasChanges: boolean = false;

    goApplicationPurposeCodeEnum = GoApplicationPurposeCodeEnum;

    readonly regExPattern = RegExPattern;
    readonly htmlInputConstants = HtmlInputConstants;
    readonly imask = IMaskConstants;

    get applicationPurposeControl(): FormControl<GoApplicationPurposeCodeEnum | null> {
        const control = this.form.controls.applicationPurpose;

        if (!control) {
            throw new Error('Application purpose control is not defined');
        }

        return control;
    }

    get callSignControl(): FormControl<string | null> {
        const control = this.form.controls.callSign;

        if (!control) {
            throw new Error('Call sign control is not defined');
        }

        return control;
    }

    get fccRegistrationNumberControl(): FormControl<string | null> {
        const control = this.form.controls.fccRegistrationNumber;

        if (!control) {
            throw new Error('FCC registration number control is not defined');
        }

        return control;
    }

    ngAfterViewInit(): void {
        // Initialize the form with the default application purpose
        this.onApplicationPurposeChange(
            GoApplicationPurposeCodeEnum.NewLicense,
        );

        this.newLicenseRadioButton.checked = true;
        this.callSignControl.setValue(''); // Prevent the callsign from appearing in both textboxes when entering a callsign, continuing, then returning back to this page
    }

    getCallSignError(): string | undefined {
        const control = this.form.controls.callSign;
        if (!control?.errors || (!control.touched && !control.dirty)) {
            return undefined;
        }

        const firstErrorKey = Object.keys(control.errors)[0];
        return PurposeOfFilingErrorMessages.callSign[
            firstErrorKey as keyof typeof PurposeOfFilingErrorMessages.callSign
        ];
    }

    /**
     * Determines whether the application purpose expansion panels should be highlighted as invalid.
     * @returns true if the expansion panels should be highlighted, false otherwise.
     */
    shouldHighlightApplicationPurposeExpansionPanel(): boolean {
        return (
            this.form.controls.applicationPurpose.invalid && this.form.touched
        );
    }

    /**
     * Handles when one of the radio buttons for application purpose is changed.
     * @param event
     */
    onRadioButtonChange(event: MatRadioChange): void {
        this.onApplicationPurposeChange(
            event.value as GoApplicationPurposeCodeEnum,
        );
    }

    /**
     * Handles when an expansion panel is clicked.
     * @param radioButton The radio button associated with the expansion panel.
     */
    onExpansionPanelClick(radioButton: MatRadioButton): void {
        radioButton.checked = true;
        this.onApplicationPurposeChange(
            radioButton.value as GoApplicationPurposeCodeEnum,
        );
    }

    /**
     * Handles cascading changes when the application purpose is changed.
     * @param newApplicationPurpose the new application purpose code.
     * @throws Error if the application purpose control is not defined.
     */
    onApplicationPurposeChange(
        newApplicationPurpose: GoApplicationPurposeCodeEnum,
    ): void {
        const applicationPurposeControl = this.form.controls.applicationPurpose;

        if (!applicationPurposeControl) {
            throw new Error('Application purpose control is not defined');
        }

        applicationPurposeControl.setValue(newApplicationPurpose);

        this.updateAllExpansionPanelsState();

        this.goApplicationPreparationService.updateGoApplicationFormControls(
            this.form as unknown as FormGroup<
                FormGroupType<GoApplicationRequestFormModel>
            >,
        );
    }

    /**
     * Updates the state of all expansion panels based on the selected application purpose.
     * This method opens the expansion panel corresponding to the selected application purpose
     * and closes the others.
     */
    private updateAllExpansionPanelsState(): void {
        const applicationPurpose = this.applicationPurposeControl.value;

        // Keep track of which expansion panels to close.
        const expansionPanelsToClose = [];

        if (applicationPurpose === GoApplicationPurposeCodeEnum.NewLicense) {
            this.newLicenseExpansionPanel.open();
        } else {
            expansionPanelsToClose.push(this.newLicenseExpansionPanel);
        }

        if (applicationPurpose === GoApplicationPurposeCodeEnum.NewLocation) {
            this.newLocationExpansionPanel.open();
        } else {
            expansionPanelsToClose.push(this.newLocationExpansionPanel);
        }

        if (
            applicationPurpose === GoApplicationPurposeCodeEnum.NewFrequencies
        ) {
            this.newFrequenciesExpansionPanel.open();
        } else {
            expansionPanelsToClose.push(this.newFrequenciesExpansionPanel);
        }

        // Close all expansion panels that are not the currently selected one
        expansionPanelsToClose.forEach((panel) => panel.close());
    }

    private setCallSignHasNoLicenseDataError(): void {
        this.callSignControl.setErrors({
            notFound: true,
            ...this.callSignControl.errors,
        });

        this.callSignControl.markAsTouched();
        this.callSignControl.markAsDirty();
    }

    protected override shouldSubmit(): boolean {
        return false;
    }

    protected override async beforeStepContinue(): Promise<boolean> {
        if (
            this.applicationPurposeControl.value ===
            GoApplicationPurposeCodeEnum.NewLicense
        ) {
            return true;
        }

        const callSign = this.callSignControl.value?.trim() ?? '';

        if (!callSign) {
            this.setCallSignHasNoLicenseDataError();

            return false;
        }

        try {
            return await firstValueFrom(
                this.ulsService.hasLicenseData(callSign).pipe(
                    takeUntil(this.destroy$),
                    tap((response) => {
                        if (!response.hasLicenseData) {
                            this.setCallSignHasNoLicenseDataError();
                        } else {
                            // Clear any previous notFound error while keeping validator-driven errors in sync.
                            this.callSignControl.updateValueAndValidity({
                                emitEvent: false,
                            });
                        }
                    }),
                    switchMap((response) => of(response.hasLicenseData)),
                ),
            );
        } catch {
            this.setCallSignHasNoLicenseDataError();

            return false;
        }
    }

    protected override patchFormWithGoApplicationRequestFormValue(): void {
        this.form.patchValue(this.goApplicationRequestForm.value);
    }

    protected override scrollToFirstInvalidElement(): void {
        // No need to implement, this page is not long enough to require scrolling
    }
}
